import type { Document } from "../../core/document/Document";
import type { Layer, RasterLayer, GroupLayer, ShapeLayer, SolidFillLayer, GradientFillLayer, PatternFillLayer, BackgroundLayer, ArtboardLayer } from "../../core/document/Layer";
import type { TypeLayer } from "../../core/document/TypeLayer";
import { compositeLayer, nativeCompositeOp } from "../../core/pixel/blend";
import { encodePsd as encodePsdCore } from "../../core/io/psd/encodePsd";
import { encodeTiff } from "./decodeImage";

export type ExportFormat = "image/png" | "image/jpeg" | "image/webp";

export async function exportSvg(doc: Document): Promise<string> {
  let paths = "";
  for (const layer of doc.layers) {
    paths += layerToSvg(layer);
  }
  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${doc.width}" height="${doc.height}" viewBox="0 0 ${doc.width} ${doc.height}">
${paths}
</svg>`;
}

function layerToSvg(layer: Layer): string {
  if (!layer.visible || layer.opacity <= 0) return "";

  if (layer.kind === "group") {
    let children = "";
    for (const child of (layer as GroupLayer).children) {
      children += layerToSvg(child);
    }
    return children ? `<g opacity="${layer.opacity}">${children}</g>\n` : "";
  }

  if (layer.kind === "shape") {
    return shapeToSvg(layer as ShapeLayer);
  }

  if (layer.kind === "type") {
    return typeToSvg(layer as TypeLayer);
  }

  if (layer.kind === "solid-fill") {
    const f = layer as SolidFillLayer;
    const { r, g, b, a } = f.color;
    return `<rect width="100%" height="100%" fill="rgba(${r},${g},${b},${a})" opacity="${layer.opacity}"/>\n`;
  }

  if (layer.kind === "gradient-fill") {
    return ""; // Complex gradient — skip for simplicity
  }

  if (layer.kind === "pattern-fill") {
    return ""; // Complex pattern — skip for simplicity
  }

  if (layer.kind === "background" || layer.kind === "raster") {
    return rasterLayerToSvg(layer as RasterLayer | BackgroundLayer);
  }

  if (layer.kind === "artboard") {
    const ab = layer as ArtboardLayer;
    const { r, g, b, a } = ab.backgroundColor;
    let result = `<rect x="${ab.artboardX}" y="${ab.artboardY}" width="${ab.artboardWidth}" height="${ab.artboardHeight}" fill="rgba(${r},${g},${b},${a})"/>\n`;
    for (const child of ab.children) {
      result += layerToSvg(child);
    }
    return result;
  }

  return "";
}

function rasterLayerToSvg(layer: RasterLayer | BackgroundLayer): string {
  const pixels = layer.kind === "background" ? layer.pixels : (layer as RasterLayer).pixels;
  if (!pixels || pixels.width <= 0 || pixels.height <= 0) return "";

  // Convert to base64 PNG
  const canvas = document.createElement("canvas");
  canvas.width = pixels.width;
  canvas.height = pixels.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  const imageData = ctx.createImageData(pixels.width, pixels.height);
  imageData.data.set(pixels.data);
  ctx.putImageData(imageData, 0, 0);

  const dataUrl = canvas.toDataURL("image/png");

  return `<image x="${layer.x}" y="${layer.y}" width="${pixels.width}" height="${pixels.height}" href="${dataUrl}" opacity="${layer.opacity}"/>\n`;
}

function shapeToSvg(layer: ShapeLayer): string {
  let d = "";
  for (const subpath of layer.path.subpaths) {
    if (subpath.points.length === 0) continue;
    const pts = subpath.points;
    d += `M ${pts[0]!.x} ${pts[0]!.y}`;

    for (let i = 1; i < pts.length; i++) {
      const prev = pts[i - 1]!;
      const curr = pts[i]!;
      if (prev.rightDirection && curr.leftDirection) {
        d += ` C ${prev.rightDirection.x} ${prev.rightDirection.y},${curr.leftDirection.x} ${curr.leftDirection.y},${curr.x} ${curr.y}`;
      } else {
        d += ` L ${curr.x} ${curr.y}`;
      }
    }

    if (subpath.closed && pts.length > 2) {
      const last = pts[pts.length - 1]!;
      const first = pts[0]!;
      if (last.rightDirection && first.leftDirection) {
        d += ` C ${last.rightDirection.x} ${last.rightDirection.y},${first.leftDirection.x} ${first.leftDirection.y},${first.x} ${first.y}`;
      }
      d += " Z";
    }
  }

  if (!d) return "";

  let attrs = `d="${d}"`;
  if (layer.fillColor) {
    const { r, g, b, a } = layer.fillColor;
    attrs += ` fill="rgba(${r},${g},${b},${a})"`;
  } else {
    attrs += ' fill="none"';
  }
  if (layer.strokeColor && layer.strokeWidth > 0) {
    const { r, g, b, a } = layer.strokeColor;
    attrs += ` stroke="rgba(${r},${g},${b},${a})" stroke-width="${layer.strokeWidth}"`;
    attrs += ` stroke-linecap="${layer.lineCap}" stroke-linejoin="${layer.lineJoin}"`;
  }
  if (layer.opacity < 1) attrs += ` opacity="${layer.opacity}"`;

  return `<path ${attrs}/>\n`;
}

function typeToSvg(layer: TypeLayer): string {
  const { r, g, b, a } = layer.color;
  const fill = `rgba(${r},${g},${b},${a})`;
  const lines = layer.text.split("\n");
  const lineHeight = layer.fontSize * layer.leading;
  let result = "";

  for (let i = 0; i < lines.length; i++) {
    const y = layer.y + i * lineHeight;
    let attrs = `x="${layer.x}" y="${y}" fill="${fill}" font-family="${layer.fontFamily}" font-size="${layer.fontSize}"`;
    if (layer.fontWeight > 400) attrs += ` font-weight="${layer.fontWeight}"`;
    if (layer.fontStyle === "italic") attrs += ' font-style="italic"';
    if (layer.textAlign !== "left") attrs += ` text-anchor="${layer.textAlign === "center" ? "middle" : "end"}"`;
    if (layer.opacity < 1) attrs += ` opacity="${layer.opacity}"`;
    result += `<text ${attrs}>${escapeXml(lines[i]!)}</text>\n`;
  }

  return result;
}

function escapeXml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

export async function exportBmp(doc: Document): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = doc.width;
  canvas.height = doc.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D context unavailable for export");

  for (const layer of doc.layers) {
    drawLayerToCanvas(ctx, layer);
  }

  const imageData = ctx.getImageData(0, 0, doc.width, doc.height);
  const data = imageData.data;

  const rowSize = Math.ceil((doc.width * 3) / 4) * 4;
  const pixelDataSize = rowSize * doc.height;
  const fileSize = 54 + pixelDataSize;

  const buffer = new ArrayBuffer(fileSize);
  const view = new DataView(buffer);

  view.setUint8(0, 0x42);
  view.setUint8(1, 0x4d);
  view.setUint32(2, fileSize, true);
  view.setUint32(10, 54, true);
  view.setUint32(14, 40, true);
  view.setInt32(18, doc.width, true);
  view.setInt32(22, doc.height, true);
  view.setUint16(26, 1, true);
  view.setUint16(28, 24, true);
  view.setUint32(34, pixelDataSize, true);

  for (let y = 0; y < doc.height; y++) {
    for (let x = 0; x < doc.width; x++) {
      const srcIdx = ((doc.height - 1 - y) * doc.width + x) * 4;
      const dstIdx = 54 + y * rowSize + x * 3;
      view.setUint8(dstIdx, data[srcIdx + 2]!);
      view.setUint8(dstIdx + 1, data[srcIdx + 1]!);
      view.setUint8(dstIdx + 2, data[srcIdx]!);
    }
  }

  return new Blob([buffer], { type: "image/bmp" });
}

export async function exportGif(doc: Document): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = doc.width;
  canvas.height = doc.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D context unavailable for export");

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, doc.width, doc.height);
  for (const layer of doc.layers) {
    drawLayerToCanvas(ctx, layer);
  }

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("GIF encode failed"))),
      "image/gif",
    );
  });
}

export interface GifFrame {
  pixels: Uint8ClampedArray;
  width: number;
  height: number;
  delayMs?: number;
}

export async function exportAnimatedGif(
  frames: GifFrame[],
  loopCount = 0,
  defaultDelayMs = 100,
): Promise<Blob> {
  if (frames.length === 0) throw new Error("No frames to encode");

  const width = frames[0]!.width;
  const height = frames[0]!.height;

  // Build frames as canvas images and get them as GIF via browser
  // For multi-frame GIF, we use a minimal GIF89a encoder
  const globalColorTable = buildGlobalColorTable(frames);
  const globalColorTableSize = globalColorTable.length / 3;
  const gctFlag = 1;
  const colorResolution = 7;
  const sortFlag = 0;
  const sizeOfGlobalColorTable = Math.log2(globalColorTableSize) - 1;

  const parts: Uint8Array[] = [];

  // Header
  parts.push(new Uint8Array([0x47, 0x49, 0x46, 0x38, 0x39, 0x61])); // GIF89a

  // Logical Screen Descriptor
  const lsd = new Uint8Array(7);
  lsd[0] = width & 0xff;
  lsd[1] = (width >> 8) & 0xff;
  lsd[2] = height & 0xff;
  lsd[3] = (height >> 8) & 0xff;
  lsd[4] = (gctFlag << 7) | (colorResolution << 4) | (sortFlag << 3) | sizeOfGlobalColorTable;
  lsd[5] = 0; // Background color index
  lsd[6] = 0; // Pixel aspect ratio
  parts.push(lsd);

  // Global Color Table
  parts.push(globalColorTable);

  // Netscape Extension for looping
  if (loopCount >= 0) {
    const netscape = new Uint8Array([
      0x21, 0xff, 0x0b, // Extension introducer, label, size
      0x4e, 0x45, 0x54, 0x53, 0x43, 0x41, 0x50, 0x45, 0x32, 0x2e, 0x30, // NETSCAPE2.0
      0x03, 0x01, // Sub-block: application identifier
      loopCount & 0xff, (loopCount >> 8) & 0xff, // Loop count
      0x00, // Block terminator
    ]);
    parts.push(netscape);
  }

  // Image data for each frame
  for (let f = 0; f < frames.length; f++) {
    const frame = frames[f]!;
    const delay = frame.delayMs ?? defaultDelayMs;
    const delayCs = Math.round(delay / 10); // Convert to centiseconds

    // Graphics Control Extension
    const gce = new Uint8Array([
      0x21, 0xf9, // Extension introducer, graphic control label
      0x04, // Block size
      0x00, // Packed byte (no transparency)
      delayCs & 0xff, (delayCs >> 8) & 0xff, // Delay time
      0x00, // Transparent color index
      0x00, // Block terminator
    ]);
    parts.push(gce);

    // Image Descriptor
    const id = new Uint8Array(10);
    id[0] = 0x2c; // Image separator
    id[1] = 0x00; id[2] = 0x00; // Left position
    id[3] = 0x00; id[4] = 0x00; // Top position
    id[5] = frame.width & 0xff; id[6] = (frame.width >> 8) & 0xff;
    id[7] = frame.height & 0xff; id[8] = (frame.height >> 8) & 0xff;
    id[9] = 0x00; // No local color table
    parts.push(id);

    // LZW-compressed image data
    const lzwData = lzwEncode(frame.pixels, frame.width, frame.height, globalColorTable);
    parts.push(lzwData);
  }

  // Trailer
  parts.push(new Uint8Array([0x3b]));

  // Combine all parts
  const totalSize = parts.reduce((sum, p) => sum + p.length, 0);
  const result = new Uint8Array(totalSize);
  let offset = 0;
  for (const part of parts) {
    result.set(part, offset);
    offset += part.length;
  }

  return new Blob([result], { type: "image/gif" });
}

function buildGlobalColorTable(frames: GifFrame[]): Uint8Array {
  // Simple median-cut quantization to 256 colors
  const allPixels: number[][] = [];
  for (const frame of frames) {
    for (let i = 0; i < frame.pixels.length; i += 4) {
      allPixels.push([frame.pixels[i]!, frame.pixels[i + 1]!, frame.pixels[i + 2]!]);
    }
  }

  // Use a simple approach: sample colors and build a palette
  const palette = new Map<string, number[]>();
  const step = Math.max(1, Math.floor(allPixels.length / 4096));
  for (let i = 0; i < allPixels.length; i += step) {
    const pixel = allPixels[i]!;
    // Quantize to reduce palette size
    const qr = Math.round((pixel[0] ?? 0) / 32) * 32;
    const qg = Math.round((pixel[1] ?? 0) / 32) * 32;
    const qb = Math.round((pixel[2] ?? 0) / 32) * 32;
    const key = `${qr},${qg},${qb}`;
    if (!palette.has(key)) {
      palette.set(key, [qr, qg, qb]);
    }
  }

  // Fill up to 256 colors (power of 2)
  const colors = Array.from(palette.values());
  while (colors.length < 256) {
    colors.push([0, 0, 0]);
  }

  // Build the color table (must be power of 2, up to 256)
  const tableSize = Math.pow(2, Math.ceil(Math.log2(Math.min(colors.length, 256))));
  const table = new Uint8Array(tableSize * 3);
  for (let i = 0; i < tableSize; i++) {
    const c = i < colors.length ? colors[i]! : [0, 0, 0];
    table[i * 3] = c[0]!;
    table[i * 3 + 1] = c[1]!;
    table[i * 3 + 2] = c[2]!;
  }

  return table;
}

function lzwEncode(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  colorTable: Uint8Array,
): Uint8Array {
  // Build a lookup for color -> index
  const colorToIndex = new Map<string, number>();
  const tableSize = colorTable.length / 3;
  for (let i = 0; i < tableSize; i++) {
    const key = `${colorTable[i * 3]},${colorTable[i * 3 + 1]},${colorTable[i * 3 + 2]}`;
    colorToIndex.set(key, i);
  }

  // Convert pixels to indices
  const indices: number[] = [];
  for (let i = 0; i < pixels.length; i += 4) {
    const key = `${pixels[i]},${pixels[i + 1]},${pixels[i + 2]}`;
    let idx = colorToIndex.get(key);
    if (idx === undefined) {
      // Find nearest color
      let bestDist = Infinity;
      let bestIdx = 0;
      for (let j = 0; j < tableSize; j++) {
        const dr = pixels[i]! - colorTable[j * 3]!;
        const dg = pixels[i + 1]! - colorTable[j * 3 + 1]!;
        const db = pixels[i + 2]! - colorTable[j * 3 + 2]!;
        const dist = dr * dr + dg * dg + db * db;
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = j;
        }
      }
      idx = bestIdx;
    }
    indices.push(idx);
  }

  // Simple LZW compression
  const minCodeSize = 8;
  const clearCode = 1 << minCodeSize;
  const eoiCode = clearCode + 1;
  let codeSize = minCodeSize + 1;
  let nextCode = eoiCode + 1;

  const dictionary = new Map<string, number>();
  for (let i = 0; i < clearCode; i++) {
    dictionary.set(String(i), i);
  }

  const output: number[] = [];

  function writeCode(code: number, size: number): void {
    // This is handled by the encoder below
  }

  // Simplified LZW - output codes as bytes (not fully compressed for simplicity)
  // For a production encoder, use a proper LZW implementation
  output.push(minCodeSize); // LZW minimum code size

  // Clear code
  let bitBuffer = 0;
  let bitCount = 0;
  const bytes: number[] = [];

  function emitCode(code: number, size: number): void {
    bitBuffer |= code << bitCount;
    bitCount += size;
    while (bitCount >= 8) {
      bytes.push(bitBuffer & 0xff);
      bitBuffer >>= 8;
      bitCount -= 8;
    }
  }

  emitCode(clearCode, codeSize);

  if (indices.length > 0) {
    let w = String(indices[0]!);
    for (let i = 1; i < indices.length; i++) {
      const k = String(indices[i]!);
      const wk = w + "," + k;
      if (dictionary.has(wk)) {
        w = wk;
      } else {
        emitCode(dictionary.get(w)!, codeSize);
        if (nextCode < 4096) {
          dictionary.set(wk, nextCode);
          if (nextCode >= (1 << codeSize) && codeSize < 12) {
            codeSize++;
          }
          nextCode++;
        } else {
          emitCode(clearCode, codeSize);
          dictionary.clear();
          for (let j = 0; j < clearCode; j++) {
            dictionary.set(String(j), j);
          }
          nextCode = eoiCode + 1;
          codeSize = minCodeSize + 1;
        }
        w = k;
      }
    }
    emitCode(dictionary.get(w)!, codeSize);
  }

  emitCode(eoiCode, codeSize);

  // Flush remaining bits
  if (bitCount > 0) {
    bytes.push(bitBuffer & 0xff);
  }

  // Package into sub-blocks
  const result: number[] = [];
  let pos = 0;
  while (pos < bytes.length) {
    const blockSize = Math.min(255, bytes.length - pos);
    result.push(blockSize);
    for (let i = 0; i < blockSize; i++) {
      result.push(bytes[pos + i]!);
    }
    pos += blockSize;
  }
  result.push(0); // Block terminator

  return new Uint8Array(result);
}

export async function exportIco(doc: Document): Promise<Blob> {
  const size = Math.min(doc.width, doc.height, 256);

  // Crop offsets for non-square documents: centre-crop to the smaller dimension.
  const sx = doc.width > doc.height ? (doc.width - doc.height) / 2 : 0;
  const sy = doc.height > doc.width ? (doc.height - doc.width) / 2 : 0;
  const srcSize = Math.min(doc.width, doc.height);

  // Render the full document into an intermediate canvas at document size.
  const full = document.createElement("canvas");
  full.width = doc.width;
  full.height = doc.height;
  const fullCtx = full.getContext("2d");
  if (!fullCtx) throw new Error("2D context unavailable for export");
  fullCtx.fillStyle = "#000000";
  fullCtx.fillRect(0, 0, doc.width, doc.height);
  for (const layer of doc.layers) {
    drawLayerToCanvas(fullCtx, layer);
  }

  // Crop the square centre region into the output canvas.
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D context unavailable for export");
  ctx.drawImage(full, sx, sy, srcSize, srcSize, 0, 0, size, size);

  const imageData = ctx.getImageData(0, 0, size, size);
  const data = imageData.data;
  const wByte = size === 256 ? 0 : size;
  const hByte = size === 256 ? 0 : size;
  const pixelDataSize = size * size * 4;
  const maskSize = Math.ceil(size / 32) * 4 * size;
  const totalSize = 6 + 16 + pixelDataSize + maskSize;

  const buffer = new ArrayBuffer(totalSize);
  const view = new DataView(buffer);
  const out = new Uint8Array(buffer);

  view.setUint16(0, 0, true);
  view.setUint16(2, 1, true);
  view.setUint16(4, 1, true);

  out[6] = wByte;
  out[7] = hByte;
  view.setUint16(10, 1, true);
  view.setUint16(12, 32, true);
  view.setUint32(14, pixelDataSize + maskSize, true);
  view.setUint32(18, 22, true);

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const srcIdx = ((size - 1 - y) * size + x) * 4;
      const dstIdx = 22 + (y * size + x) * 4;
      out[dstIdx] = data[srcIdx]!;
      out[dstIdx + 1] = data[srcIdx + 1]!;
      out[dstIdx + 2] = data[srcIdx + 2]!;
      out[dstIdx + 3] = data[srcIdx + 3]!;
    }
  }

  const maskRowBytes = Math.ceil(size / 32) * 4;
  const maskOffset = 22 + pixelDataSize;
  for (let y = 0; y < size; y++) {
    for (let byteIdx = 0; byteIdx < maskRowBytes; byteIdx++) {
      out[maskOffset + y * maskRowBytes + byteIdx] = 0;
    }
  }

  return new Blob([buffer], { type: "image/x-icon" });
}

export async function encodePsd(
  doc: Document,
  opts?: {
    guides?: ReadonlyArray<{ kind: "horizontal" | "vertical"; position: number }>;
    iccProfile?: Uint8Array;
  },
): Promise<Uint8Array> {
  return encodePsdCore(doc, opts);
}

export async function exportDocument(
  doc: Document,
  format: ExportFormat,
  quality = 0.92,
): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = doc.width;
  canvas.height = doc.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D context unavailable for export");

  if (format === "image/jpeg") {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, doc.width, doc.height);
  }

  for (const layer of doc.layers) {
    drawLayerToCanvas(ctx, layer);
  }

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Encode failed"))),
      format,
      quality,
    );
  });
}

function drawLayerToCanvas(ctx: CanvasRenderingContext2D, layer: Layer): void {
  if (!layer.visible || layer.opacity <= 0) return;

  if (layer.kind === "group") {
    ctx.save();
    ctx.globalAlpha *= layer.opacity;
    for (const child of (layer as GroupLayer).children) {
      drawLayerToCanvas(ctx, child);
    }
    ctx.restore();
    return;
  }

  if (layer.kind === "shape") {
    drawShapeToCanvas(ctx, layer as ShapeLayer);
    return;
  }

  if (layer.kind === "type") {
    drawTypeToCanvas(ctx, layer as TypeLayer);
    return;
  }

  if (layer.kind === "solid-fill") {
    const f = layer as SolidFillLayer;
    ctx.save();
    ctx.globalAlpha = layer.opacity;
    const { r, g, b, a } = f.color;
    ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.restore();
    return;
  }

  if (layer.kind === "gradient-fill") {
    const f = layer as GradientFillLayer;
    if (f.stops.length === 0) return;
    ctx.save();
    ctx.globalAlpha = layer.opacity;
    const w = ctx.canvas.width;
    const h = ctx.canvas.height;
    const cx = w / 2;
    const cy = h / 2;
    const stops = f.reverse ? [...f.stops].reverse().map((s, i, arr) => ({
      ...s,
      position: 1 - s.position,
    })) : f.stops;
    const sorted = [...stops].sort((a, b) => a.position - b.position);
    let grad: CanvasGradient;
    const angleRad = ((f.angle ?? 90) * Math.PI) / 180;
    if (f.gradientType === "radial") {
      const r = (Math.max(w, h) / 2) * (f.scale / 100);
      grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    } else if (f.gradientType === "angle") {
      // Angle gradients can't be done with CanvasGradient — approximate as linear
      const dx = Math.cos(angleRad) * (w / 2);
      const dy = -Math.sin(angleRad) * (h / 2);
      grad = ctx.createLinearGradient(cx - dx, cy - dy, cx + dx, cy + dy);
    } else if (f.gradientType === "reflected") {
      // Reflected: mirror from center outward along the angle
      const dx = Math.cos(angleRad) * (w / 2);
      const dy = -Math.sin(angleRad) * (h / 2);
      grad = ctx.createLinearGradient(cx - dx, cy - dy, cx + dx, cy + dy);
    } else if (f.gradientType === "diamond") {
      // Diamond: approximate as radial from center
      const r = (Math.max(w, h) / 2) * (f.scale / 100);
      grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    } else {
      // linear (default)
      const scale = (f.scale ?? 100) / 100;
      const dx = Math.cos(angleRad) * (w / 2) * scale;
      const dy = -Math.sin(angleRad) * (h / 2) * scale;
      grad = ctx.createLinearGradient(cx - dx, cy - dy, cx + dx, cy + dy);
    }
    for (const s of sorted) {
      const { r, g, b, a } = s.color;
      grad.addColorStop(Math.max(0, Math.min(1, s.position)), `rgba(${r},${g},${b},${a})`);
    }
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
    return;
  }

  if (layer.kind === "pattern-fill") {
    const f = layer as PatternFillLayer;
    if (f.pattern && f.pattern.width > 0 && f.pattern.height > 0) {
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      const patCanvas = document.createElement("canvas");
      patCanvas.width = f.pattern.width;
      patCanvas.height = f.pattern.height;
      const pctx = patCanvas.getContext("2d");
      if (pctx) {
        const imageData = pctx.createImageData(f.pattern.width, f.pattern.height);
        imageData.data.set(f.pattern.pixels);
        pctx.putImageData(imageData, 0, 0);
        const pattern = ctx.createPattern(patCanvas, "repeat");
        if (pattern) {
          ctx.fillStyle = pattern;
          ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        }
      }
      ctx.restore();
    }
    return;
  }

  if (layer.kind === "background") {
    const bg = layer as BackgroundLayer;
    const off = document.createElement("canvas");
    off.width = bg.pixels.width;
    off.height = bg.pixels.height;
    const offCtx = off.getContext("2d");
    if (!offCtx) return;
    const imageData = offCtx.createImageData(bg.pixels.width, bg.pixels.height);
    imageData.data.set(bg.pixels.data);
    offCtx.putImageData(imageData, 0, 0);
    ctx.save();
    ctx.globalAlpha = layer.opacity;
    ctx.drawImage(off, bg.x, bg.y);
    ctx.restore();
    return;
  }

  if (layer.kind === "artboard") {
    const ab = layer as ArtboardLayer;
    ctx.save();
    const { r, g, b, a } = ab.backgroundColor;
    ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
    ctx.fillRect(ab.artboardX, ab.artboardY, ab.artboardWidth, ab.artboardHeight);
    ctx.beginPath();
    ctx.rect(ab.artboardX, ab.artboardY, ab.artboardWidth, ab.artboardHeight);
    ctx.clip();
    for (const child of ab.children) {
      drawLayerToCanvas(ctx, child);
    }
    ctx.restore();
    return;
  }

  if (layer.kind !== "raster") return;
  const raster = layer as RasterLayer;

  const off = document.createElement("canvas");
  off.width = raster.pixels.width;
  off.height = raster.pixels.height;
  const offCtx = off.getContext("2d");
  if (!offCtx) return;
  const imageData = offCtx.createImageData(raster.pixels.width, raster.pixels.height);
  imageData.data.set(raster.pixels.data);
  offCtx.putImageData(imageData, 0, 0);

  const nativeOp = nativeCompositeOp(layer.blendMode);
  if (nativeOp !== null) {
    ctx.save();
    ctx.globalAlpha = layer.opacity;
    ctx.globalCompositeOperation = nativeOp;
    ctx.drawImage(off, layer.x, layer.y);
    ctx.restore();
    return;
  }

  // Offscreen pixel composite for modes Canvas2D can't do.
  compositeOffscreenExport(ctx, raster, off);
}

function drawShapeToCanvas(ctx: CanvasRenderingContext2D, layer: ShapeLayer): void {
  ctx.save();
  ctx.globalAlpha = layer.opacity;
  ctx.translate(layer.x, layer.y);

  for (const subpath of layer.path.subpaths) {
    if (subpath.points.length === 0) continue;
    ctx.beginPath();
    const pts = subpath.points;
    ctx.moveTo(pts[0]!.x, pts[0]!.y);
    for (let i = 1; i < pts.length; i++) {
      const prev = pts[i - 1]!;
      const curr = pts[i]!;
      if (prev.rightDirection && curr.leftDirection) {
        ctx.bezierCurveTo(prev.rightDirection.x, prev.rightDirection.y, curr.leftDirection.x, curr.leftDirection.y, curr.x, curr.y);
      } else {
        ctx.lineTo(curr.x, curr.y);
      }
    }
    if (subpath.closed && pts.length > 2) ctx.closePath();

    if (layer.fillColor) {
      const { r, g, b, a } = layer.fillColor;
      ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
      ctx.fill();
    }
    if (layer.strokeColor && layer.strokeWidth > 0) {
      const { r, g, b, a } = layer.strokeColor;
      ctx.strokeStyle = `rgba(${r},${g},${b},${a})`;
      ctx.lineWidth = layer.strokeWidth;
      ctx.lineCap = layer.lineCap;
      ctx.lineJoin = layer.lineJoin;
      ctx.stroke();
    }
  }
  ctx.restore();
}

function drawTypeToCanvas(ctx: CanvasRenderingContext2D, layer: TypeLayer): void {
  if (layer.text.length === 0) return;
  ctx.save();
  ctx.globalAlpha = layer.opacity;
  const { r, g, b, a } = layer.color;
  ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
  let weight = layer.fontWeight;
  if ((layer as any).fauxBold) weight = Math.min(900, weight + 300);
  let style = layer.fontStyle;
  if ((layer as any).fauxItalic && style === "normal") style = "italic";
  ctx.font = `${style} ${weight} ${layer.fontSize}px "${layer.fontFamily}"`;
  ctx.textAlign = layer.textAlign === "justify" ? "left" : layer.textAlign;
  ctx.textBaseline = layer.textBaseline;
  const lines = layer.text.split("\n");
  const lineHeight = layer.fontSize * layer.leading;
  let startY = layer.y;
  if (layer.textBaseline === "middle") startY -= (lines.length * lineHeight) / 2;
  else if (layer.textBaseline === "bottom") startY -= lines.length * lineHeight;
  for (let i = 0; i < lines.length; i++) {
    let text = lines[i]!;
    if ((layer as any).allCaps) text = text.toUpperCase();
    ctx.fillText(text, layer.x, startY + i * lineHeight);
  }
  ctx.restore();
}

function compositeOffscreenExport(
  ctx: CanvasRenderingContext2D,
  layer: RasterLayer,
  srcBitmap: HTMLCanvasElement,
): void {
  const w = srcBitmap.width;
  const h = srcBitmap.height;
  if (w <= 0 || h <= 0) return;

  const temp = document.createElement("canvas");
  temp.width = w;
  temp.height = h;
  const tctx = temp.getContext("2d");
  if (!tctx) return;

  // Pull destination patch.
  const t = ctx.getTransform();
  const devX = t.a * layer.x + t.c * layer.y + t.e;
  const devY = t.b * layer.x + t.d * layer.y + t.f;
  const devW = Math.max(1, Math.round(w * t.a + h * t.c));
  const devH = Math.max(1, Math.round(w * t.b + h * t.d));
  tctx.setTransform(1, 0, 0, 1, 0, 0);
  tctx.clearRect(0, 0, w, h);
  tctx.drawImage(ctx.canvas, Math.round(devX), Math.round(devY), devW, devH, 0, 0, w, h);
  const dstImage = tctx.getImageData(0, 0, w, h);

  const sctx = srcBitmap.getContext("2d");
  if (!sctx) return;
  const srcImage = sctx.getImageData(0, 0, w, h);

  compositeLayer(dstImage.data, srcImage.data, layer.blendMode, layer.opacity);

  tctx.putImageData(dstImage, 0, 0);

  ctx.save();
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = "source-over";
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(layer.x, layer.y, w, h);
  ctx.drawImage(temp, layer.x, layer.y, w, h);
  ctx.restore();
}

// ── Explicit PNG encoder (minimal, deflate-based) ──────────────

export async function encodePngExplicit(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  compression = 6,
): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D context unavailable");
  const imageData = ctx.createImageData(width, height);
  imageData.data.set(pixels);
  ctx.putImageData(imageData, 0, 0);
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("PNG encode failed"))),
      "image/png",
    );
  });
}

// ── Explicit JPEG encoder with quality control ──────────────

export async function encodeJpegExplicit(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  quality = 0.92,
  grayscale = false,
): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D context unavailable");
  const imageData = ctx.createImageData(width, height);
  if (grayscale) {
    for (let i = 0; i < pixels.length; i += 4) {
      const lum = Math.round(0.299 * pixels[i]! + 0.587 * pixels[i + 1]! + 0.114 * pixels[i + 2]!);
      imageData.data[i] = lum;
      imageData.data[i + 1] = lum;
      imageData.data[i + 2] = lum;
      imageData.data[i + 3] = 255;
    }
  } else {
    imageData.data.set(pixels);
    for (let i = 3; i < imageData.data.length; i += 4) imageData.data[i] = 255;
  }
  ctx.putImageData(imageData, 0, 0);
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("JPEG encode failed"))),
      "image/jpeg",
      quality,
    );
  });
}

// ── Explicit WebP encoder ──────────────

export async function encodeWebpExplicit(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  quality = 0.92,
): Promise<Blob | null> {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D context unavailable");
  const imageData = ctx.createImageData(width, height);
  imageData.data.set(pixels);
  ctx.putImageData(imageData, 0, 0);
  const webpSupported = canvas.toDataURL("image/webp").indexOf("data:image/webp") === 0;
  if (!webpSupported) return null;
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("WebP encode failed"))),
      "image/webp",
      quality,
    );
  });
}

// ── Explicit AVIF encoder ──────────────

export async function encodeAvifExplicit(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  quality = 0.92,
): Promise<Blob | null> {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D context unavailable");
  const imageData = ctx.createImageData(width, height);
  imageData.data.set(pixels);
  ctx.putImageData(imageData, 0, 0);
  const avifSupported = canvas.toDataURL("image/avif").indexOf("data:image/avif") === 0;
  if (!avifSupported) return null;
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("AVIF encode failed"))),
      "image/avif",
      quality,
    );
  });
}

// ── Basic PDF encoder (embeds rasterized document as JPEG) ──────────────

export async function exportPdf(doc: Document): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = doc.width;
  canvas.height = doc.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D context unavailable for PDF export");

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, doc.width, doc.height);
  for (const layer of doc.layers) {
    drawLayerToCanvas(ctx, layer);
  }

  // Get JPEG data
  const jpegBlob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("JPEG encode failed"))),
      "image/jpeg",
      0.92,
    );
  });

  const jpegBytes = new Uint8Array(await jpegBlob.arrayBuffer());
  const jpegLength = jpegBytes.length;

  // Build minimal PDF
  const pdfParts: Array<string | Uint8Array> = [];
  let offset = 0;
  const offsets: number[] = [];

  // Header
  const header = "%PDF-1.4\n";
  pdfParts.push(header);
  offset += header.length;

  // Object 1: Catalog
  offsets.push(offset);
  const obj1 = "1 0 obj\n<</Type/Catalog/Pages 2 0 R>>\nendobj\n";
  pdfParts.push(obj1);
  offset += obj1.length;

  // Object 2: Pages
  offsets.push(offset);
  const obj2 = `2 0 obj\n<</Type/Pages/Kids[3 0 R]/Count 1>>\nendobj\n`;
  pdfParts.push(obj2);
  offset += obj2.length;

  // Object 3: Page
  offsets.push(offset);
  const obj3 = `3 0 obj\n<</Type/Page/Parent 2 0 R/MediaBox[0 0 ${doc.width} ${doc.height}]/Contents 4 0 R/Resources<</XObject<</Im0 5 0 R>>>>>>\nendobj\n`;
  pdfParts.push(obj3);
  offset += obj3.length;

  // Object 4: Content stream
  const streamContent = `q ${doc.width} 0 0 ${doc.height} 0 0 cm /Im0 Do Q`;
  const obj4Header = `4 0 obj\n<</Length ${streamContent.length}>>\nstream\n`;
  const obj4Footer = `\nendstream\nendobj\n`;
  offsets.push(offset);
  pdfParts.push(obj4Header);
  offset += obj4Header.length;
  pdfParts.push(new TextEncoder().encode(streamContent));
  offset += streamContent.length;
  pdfParts.push(obj4Footer);
  offset += obj4Footer.length;

  // Object 5: Image XObject
  offsets.push(offset);
  const obj5 = `5 0 obj\n<</Type/XObject/Subtype/Image/Width ${doc.width}/Height ${doc.height}/ColorSpace/DeviceRGB/BitsPerComponent 8/Filter/DCTDecode/Length ${jpegLength}>>\nstream\n`;
  pdfParts.push(obj5);
  offset += obj5.length;
  pdfParts.push(jpegBytes);
  offset += jpegLength;
  const obj5Footer = `\nendstream\nendobj\n`;
  pdfParts.push(obj5Footer);
  offset += obj5Footer.length;

  // Cross-reference table
  const xrefOffset = offset;
  const xrefHeader = `xref\n0 6\n0000000000 65535 f \n`;
  pdfParts.push(xrefHeader);
  offset += xrefHeader.length;

  for (let i = 0; i < offsets.length; i++) {
    const line = `${String(offsets[i]!).padStart(10, "0")} 00000 n \n`;
    pdfParts.push(line);
    offset += line.length;
  }

  // Trailer
  const trailer = `trailer\n<</Size 6/Root 1 0 R>>\nstartxref\n${xrefOffset}\n%%EOF`;
  pdfParts.push(trailer);

  // Combine all parts
  const totalSize = pdfParts.reduce((sum, p) => sum + (typeof p === "string" ? p.length : p.length), 0);
  const result = new Uint8Array(totalSize);
  let pos = 0;
  for (const part of pdfParts) {
    if (typeof part === "string") {
      const bytes = new TextEncoder().encode(part);
      result.set(bytes, pos);
      pos += bytes.length;
    } else {
      result.set(part, pos);
      pos += part.length;
    }
  }

  return new Blob([result], { type: "application/pdf" });
}

// ── Export Layers to Files helpers ────────────────────────────────────────

/**
 * A single layer entry returned by collectExportLayers.
 * `pixels` is an RGBA ImageData representing the layer's rasterized content at
 * document coordinates. Non-raster layers (type, shape, adjustment, fill …)
 * are rendered by drawing them onto a doc-sized canvas so the caller gets
 * consistent pixel data regardless of layer kind.
 */
export interface ExportedLayer {
  name: string;
  /** Rasterized RGBA pixels at the layer's natural bounds. */
  imageData: ImageData;
  /** Left edge in document space (0 for rasterized non-raster layers). */
  x: number;
  /** Top edge in document space (0 for rasterized non-raster layers). */
  y: number;
  width: number;
  height: number;
}

/**
 * Collect every visible, exportable layer in `doc` as a flat array of
 * `ExportedLayer` entries. Recurse into group and artboard children; rasterize
 * non-raster layers (type, shape, adjustment, fill …) by rendering them onto a
 * doc-sized canvas. The caller is responsible for actually downloading/saving.
 */
export function collectExportLayers(doc: Document): ExportedLayer[] {
  const results: ExportedLayer[] = [];
  for (const layer of doc.layers) {
    collectLayerExport(layer, doc, results);
  }
  return results;
}

function collectLayerExport(layer: Layer, doc: Document, out: ExportedLayer[]): void {
  if (!layer.visible) return;

  // Groups and artboards: recurse into children instead of exporting the group itself.
  if (layer.kind === "group") {
    for (const child of (layer as GroupLayer).children) {
      collectLayerExport(child, doc, out);
    }
    return;
  }
  if (layer.kind === "artboard") {
    for (const child of (layer as ArtboardLayer).children) {
      collectLayerExport(child, doc, out);
    }
    return;
  }

  // Raster / background: export pixel data directly.
  if (layer.kind === "raster" || layer.kind === "background") {
    const raster = layer as RasterLayer | BackgroundLayer;
    const pixels = raster.pixels;
    if (!pixels || pixels.width <= 0 || pixels.height <= 0) return;
    const canvas = document.createElement("canvas");
    canvas.width = pixels.width;
    canvas.height = pixels.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const imageData = ctx.createImageData(pixels.width, pixels.height);
    imageData.data.set(pixels.data);
    ctx.putImageData(imageData, 0, 0);
    out.push({
      name: layer.name,
      imageData: ctx.getImageData(0, 0, pixels.width, pixels.height),
      x: layer.x,
      y: layer.y,
      width: pixels.width,
      height: pixels.height,
    });
    return;
  }

  // All other layer kinds (type, shape, adjustment, solid-fill, gradient-fill,
  // pattern-fill, smart-object, frame, …): rasterize to a doc-sized canvas.
  const canvas = document.createElement("canvas");
  canvas.width = doc.width;
  canvas.height = doc.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  drawLayerToCanvas(ctx, layer);
  const imageData = ctx.getImageData(0, 0, doc.width, doc.height);
  // Skip fully transparent outputs.
  let hasPixels = false;
  for (let i = 3; i < imageData.data.length; i += 4) {
    if (imageData.data[i]! > 0) { hasPixels = true; break; }
  }
  if (!hasPixels) return;
  out.push({
    name: layer.name,
    imageData,
    x: 0,
    y: 0,
    width: doc.width,
    height: doc.height,
  });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
